import { Children, type FC, isValidElement, type ReactNode, useMemo } from "react";
import { Item } from "react-stately";

import { Icon } from "../../Icon/Icon.tsx";
import { VStack } from "../../Stack/VStack.tsx";
import { useResizableTabPanelsContext } from "../hooks/use-panel-group-context.ts";
import style from "../PanelGroup.module.css";
import { extractTabsFromChildren } from "../utils/extract-tabs-from-children.ts";
import { FixedPanelTab } from "./FixedPanelTab.tsx";
import { PanelTabs } from "./PanelTabs.tsx";

export interface FixedPanelProps {
  children: ReactNode;
  showTabsLabel?: boolean;
}

// FixedPanel component - detects if it has tab children
export const FixedPanel: FC<FixedPanelProps> = ({ children, showTabsLabel }) => {
  const context = useResizableTabPanelsContext();

  const flexStyle = useMemo(() => {
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: context.isOpen ? (context.currentSize ?? "50%") : context.closedSize,
    };
  }, [context.isOpen, context.currentSize, context.closedSize]);

  // Check if we have FixedPanelTab children
  const hasTabChildren = Children.toArray(children).some(
    (child) => isValidElement(child) && child.type === FixedPanelTab,
  );

  if (hasTabChildren) {
    // Render tab interface
    const tabs = extractTabsFromChildren(children);

    return (
      <div className={style.pane} style={flexStyle}>
        <VStack spacing={1} className={style.icontabs}>
          <PanelTabs
            items={tabs}
            onSelectionChange={context.setActiveTabId}
            selectedKey={context.activeTabId}
            tabsActions={context.tabsActions}
            showLabel={showTabsLabel}
          >
            {(tab) => (
              <Item
                key={tab.id}
                title={
                  showTabsLabel ? (
                    <VStack
                      spacing={0}
                      style={{
                        alignItems: "center",
                        fontSize: 12,
                        lineHeight: "12px",
                        fontWeight: 450,
                      }}
                    >
                      <Icon icon={tab.icon} aria-hidden="true" className={style.icon} />
                      {tab.title}
                    </VStack>
                  ) : (
                    <Icon icon={tab.icon} className={style.icon} />
                  )
                }
                textValue={tab.title}
              >
                {/* This is mandatory, but empty. Move all rendering into title */}
                {tab.children}
              </Item>
            )}
          </PanelTabs>
        </VStack>
      </div>
    );
  }

  // Render simple content
  return (
    <div className={style.pane} style={flexStyle}>
      {children}
    </div>
  );
};
